import userMemoryService from './UserMemoryService';

/**
 * User Instructions Service
 * Manages user-customizable instructions for each AI agent
 * These form the "User Instructions" layer in the context
 *
 * Users can customize how each agent behaves with free-form prompts
 */
class UserInstructionsService {
  constructor() {
    this.cache = new Map(); // componentType -> instructions
    this.defaultInstructions = this.getDefaultInstructions();
  }

  /**
   * Get default instructions for each agent type
   */
  getDefaultInstructions() {
    return {
      task: `Be concise and action-oriented. Focus on productivity and time management.
Help me prioritize tasks and stay organized.`,

      reflection: `Be thoughtful and empathetic. Ask deep questions that help me learn.
Guide me to discover insights from my experiences.`,

      course: `Be clear and educational. Explain concepts thoroughly with examples.
Adapt to my learning style and pace.`,

      project: `Be strategic and practical. Help me plan and execute effectively.
Identify risks and suggest best practices.`,

      orchestrator: `Be helpful and context-aware. Route my requests appropriately.
Understand my intent and provide relevant assistance.`,

      dashboard: `Be informative and insightful. Highlight important patterns.
Help me understand my progress and areas for improvement.`,
    };
  }

  /**
   * Initialize user instructions from memory
   * @param {string} userId
   */
  async initialize(userId) {
    try {
      // Load from user memory (preferences category)
      await userMemoryService.initialize(userId);

      const agentTypes = ['task', 'reflection', 'course', 'project', 'orchestrator', 'dashboard'];

      agentTypes.forEach((type) => {
        const customInstructions = userMemoryService.get(
          'preferences',
          `agent_instructions_${type}`
        );
        if (customInstructions) {
          this.cache.set(type, customInstructions);
        } else {
          // Use default if no custom instructions
          this.cache.set(type, this.defaultInstructions[type]);
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[UserInstructionsService] Error initializing:', error);
      // Fall back to defaults
      Object.entries(this.defaultInstructions).forEach(([type, instructions]) => {
        this.cache.set(type, instructions);
      });
    }
  }

  /**
   * Get instructions for a specific agent
   * @param {string} componentType - 'task', 'reflection', 'course', 'project', etc.
   */
  get(componentType) {
    return this.cache.get(componentType) || this.defaultInstructions[componentType] || '';
  }

  /**
   * Set custom instructions for an agent
   * @param {string} userId
   * @param {string} componentType
   * @param {string} instructions - Free-form prompt customization
   */
  async set(userId, componentType, instructions) {
    // Validate component type
    if (!this.defaultInstructions[componentType]) {
      throw new Error(`Invalid component type: ${componentType}`);
    }

    // Update cache
    this.cache.set(componentType, instructions);

    // Save to user memory
    await userMemoryService.set(
      userId,
      'preferences',
      `agent_instructions_${componentType}`,
      instructions,
      'explicit'
    );
  }

  /**
   * Reset instructions to default for an agent
   * @param {string} userId
   * @param {string} componentType
   */
  async reset(userId, componentType) {
    const defaultInstructions = this.defaultInstructions[componentType];
    if (!defaultInstructions) {
      throw new Error(`Invalid component type: ${componentType}`);
    }

    // Update cache
    this.cache.set(componentType, defaultInstructions);

    // Delete custom instructions from memory
    await userMemoryService.delete(userId, 'preferences', `agent_instructions_${componentType}`);
  }

  /**
   * Get all agent instructions (for settings UI)
   */
  getAll() {
    const instructions = {};
    Object.keys(this.defaultInstructions).forEach((type) => {
      instructions[type] = this.get(type);
    });
    return instructions;
  }

  /**
   * Check if instructions are customized (not default)
   * @param {string} componentType
   */
  isCustomized(componentType) {
    const current = this.get(componentType);
    const defaultInst = this.defaultInstructions[componentType];
    return current !== defaultInst;
  }

  /**
   * Format instructions for context
   * @param {string} componentType
   */
  formatForContext(componentType) {
    const instructions = this.get(componentType);
    if (!instructions) return '';

    return `**User Instructions for ${this.formatComponentName(componentType)} Agent:**

${instructions}

---`;
  }

  /**
   * Get agent metadata (name, description, etc.)
   */
  getAgentMetadata(componentType) {
    const metadata = {
      task: {
        name: 'Task Assistant',
        description: 'Helps you manage and prioritize your tasks',
        icon: 'fa-tasks',
        color: '#6DF2FF',
      },
      reflection: {
        name: 'Reflection Coach',
        description: 'Guides you through meaningful self-reflection',
        icon: 'fa-lightbulb',
        color: '#FFD700',
      },
      course: {
        name: 'Learning Assistant',
        description: 'Helps you understand course material',
        icon: 'fa-graduation-cap',
        color: '#4CAF50',
      },
      project: {
        name: 'Project Advisor',
        description: 'Guides your project planning and execution',
        icon: 'fa-project-diagram',
        color: '#FF6B6B',
      },
      orchestrator: {
        name: 'Main Assistant',
        description: 'Your primary AI assistant that coordinates everything',
        icon: 'fa-robot',
        color: '#9C27B0',
      },
      dashboard: {
        name: 'Dashboard Analyst',
        description: 'Provides insights about your progress and patterns',
        icon: 'fa-chart-line',
        color: '#2196F3',
      },
    };

    return (
      metadata[componentType] || {
        name: componentType,
        description: 'AI Assistant',
        icon: 'fa-robot',
        color: '#6DF2FF',
      }
    );
  }

  /**
   * Helper: Format component name for display
   */
  formatComponentName(componentType) {
    return componentType.charAt(0).toUpperCase() + componentType.slice(1);
  }

  /**
   * Export all instructions as JSON
   */
  exportInstructions() {
    const instructions = this.getAll();
    return JSON.stringify(instructions, null, 2);
  }

  /**
   * Import instructions from JSON
   * @param {string} userId
   * @param {string} jsonString
   */
  async importInstructions(userId, jsonString) {
    try {
      const instructions = JSON.parse(jsonString);

      for (const [componentType, instruction] of Object.entries(instructions)) {
        if (this.defaultInstructions[componentType]) {
          await this.set(userId, componentType, instruction);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[UserInstructionsService] Error importing instructions:', error);
      throw new Error('Invalid JSON format');
    }
  }

  /**
   * Get instruction templates/examples for users
   */
  getTemplates() {
    return {
      concise: {
        name: 'Concise & Direct',
        description: 'Short, actionable responses',
        template:
          'Keep responses brief and to the point. Focus on actionable advice. Avoid lengthy explanations.',
      },
      detailed: {
        name: 'Detailed & Thorough',
        description: 'Comprehensive explanations',
        template:
          'Provide detailed explanations with examples. Break down complex concepts. Include relevant context.',
      },
      socratic: {
        name: 'Socratic Method',
        description: 'Guide through questions',
        template:
          'Ask thoughtful questions to help me discover answers. Guide my thinking rather than giving direct answers.',
      },
      encouraging: {
        name: 'Encouraging & Supportive',
        description: 'Positive and motivating',
        template:
          'Be encouraging and supportive. Celebrate progress. Frame challenges as opportunities for growth.',
      },
      technical: {
        name: 'Technical & Precise',
        description: 'Technical accuracy focus',
        template:
          'Use precise technical terminology. Provide code examples when relevant. Focus on best practices.',
      },
      casual: {
        name: 'Casual & Friendly',
        description: 'Conversational tone',
        template:
          'Use a casual, friendly tone. Feel free to use analogies and humor. Make learning enjoyable.',
      },
    };
  }

  /**
   * Apply a template to an agent
   * @param {string} userId
   * @param {string} componentType
   * @param {string} templateKey
   */
  async applyTemplate(userId, componentType, templateKey) {
    const templates = this.getTemplates();
    const template = templates[templateKey];

    if (!template) {
      throw new Error(`Invalid template: ${templateKey}`);
    }

    await this.set(userId, componentType, template.template);
  }
}

// Singleton instance
const userInstructionsService = new UserInstructionsService();

export default userInstructionsService;
